import * as React from "react";
import { Helmet } from "react-helmet";
import PickerRaw from "react-day-picker";
import { isValid } from "date-fns";

import "react-day-picker/lib/style.css";

const Picker = fixLibImport(PickerRaw);

import { RANGE_PICKER } from "@config/testIds/rangePicker";

import { AVAILABLE_LANGS } from "@typings/globals";

import { TestWrapper } from "@ui/Atoms";
import { Caret } from "@ui/Assets/Symbolicons/Caret";

import { fixLibImport } from "@services/LibImportFix";
import { getWeekdaysShortByLang } from "@services/DateFormatter";

import { useTranslate } from "@hooks/useTranslate";
import { useBreakpoint } from "@hooks/useBreakpoint";

import {
  getStyling,
  RangePickerState,
  THEME_VARIANT,
  DayPickerMobileMonths,
} from "./RangerPicker.helpers";

interface Props {
  variant: THEME_VARIANT;
  monthTranslationKey: string;
  language: AVAILABLE_LANGS;
  selectable?: boolean;
  initialStartDate?: Date | undefined;
  initialEndDate?: Date | undefined;
  calendarStartDate?: Date | undefined;
  monthLabelVariant?: "onlyMonth" | "monthAndYear";
  isPastAvailable?: boolean;
  onDayChange: (dates: RangePickerState) => void;
  onMonthChange?: (month: Date) => void;
}

const RangePicker = (props: Props): React.ReactElement => {
  const { mobile, tablet } = useBreakpoint("v1");
  const translate = useTranslate();

  const getInitialState = (): RangePickerState => ({
    startDate: undefined,
    endDate: undefined,
    enteredTo: undefined,
  });

  const [state, setState] = React.useState<RangePickerState>(getInitialState());

  React.useEffect(() => {
    if (props.initialStartDate && props.initialEndDate) {
      setState({
        startDate: props.initialStartDate,
        endDate: props.initialEndDate,
        enteredTo: props.initialEndDate,
      });
    }
  }, []);

  const isMobile = mobile || tablet;
  const selectedDays = [
    state.startDate,
    { from: state.startDate, to: state.enteredTo },
  ];

  const weekdaysShort = getWeekdaysShortByLang(props.language);
  // move the first element (Monday) to the end of an array (react-day-picker workaround)
  const mobileWeekdaysShort = [...weekdaysShort.slice(1), weekdaysShort[0]];

  const modifiers = {
    start: state.startDate,
    end: state.endDate,
    enteredTo: state.enteredTo,
    firstOfMonth: (day: Date) => day.getDate() === 1,
    lastOfMonth: (day: Date) => {
      const currentMonth = day.getMonth();
      const lastDay = new Date(
        day.getFullYear(),
        currentMonth + 1,
        0,
      ).getDate();
      return day.getDate() === lastDay;
    },
    weekStart: {
      daysOfWeek: [1],
    },
    weekEnd: {
      daysOfWeek: [0],
    },
    hasRangeStart: (day: Date) => {
      if (state.enteredTo && state.startDate) {
        return day.getDate() === state.startDate.getDate();
      }
      return false;
    },
    ...(!props.isPastAvailable && {
      past: {
        before: new Date(),
      },
    }),
  };

  const handleDayClick = (day: Date): void => {
    const { startDate, endDate } = state;
    if (startDate && endDate && day >= startDate && day <= endDate) {
      handleReset();
      return;
    }
    if (isSelectingFirstDay(startDate, endDate, day)) {
      const payload = {
        startDate: day,
        endDate: undefined,
        enteredTo: undefined,
      };

      setState(payload);
      props.onDayChange(payload);
    } else {
      const payload = {
        ...state,
        endDate: day,
        enteredTo: day,
      };

      setState(payload);
      props.onDayChange(payload);
    }
  };

  const handleDayMouseEnter = (day: Date) => {
    const { startDate, endDate } = state;
    if (!isSelectingFirstDay(startDate, endDate, day)) {
      setState({
        ...state,
        enteredTo: day,
      });
    }
  };

  const isSelectingFirstDay = (
    from: Date | undefined,
    to: Date | undefined,
    day: Date,
  ): boolean => {
    const isBeforeFirstDay = !!(
      from && Picker.DateUtils.isDayBefore(day, from)
    );
    const isRangeSelected = !!(from && to);
    return !from || isBeforeFirstDay || isRangeSelected;
  };

  const handleReset = () => {
    setState(getInitialState());
    props.onDayChange(getInitialState());
  };

  const renderNavbar = ({
    onPreviousClick,
    onNextClick,
  }: {
    onPreviousClick: () => void;
    onNextClick: () => void;
  }) => (
    <div className="DayPicker-NavBarContainer">
      <div className="DayPicker-NavBar">
        <TestWrapper testId={RANGE_PICKER.NAV_PREV}>
          <span
            className="DayPicker-NavButtonCustom DayPicker-NavButtonCustom--prev"
            onClick={() => onPreviousClick()}
          >
            <Caret size={32} variant="solid" />
          </span>
        </TestWrapper>
        <TestWrapper testId={RANGE_PICKER.NAV_NEXT}>
          <span
            className="DayPicker-NavButtonCustom DayPicker-NavButtonCustom--next"
            onClick={() => onNextClick()}
          >
            <Caret size={32} variant="solid" />
          </span>
        </TestWrapper>
      </div>
    </div>
  );

  const renderCaption = ({ date }: { date: Date }) => {
    const month = date.getMonth();
    const { monthLabelVariant = "onlyMonth" } = props;

    return (
      <div className="DayPicker-Caption">
        {translate(`${props.monthTranslationKey}${month + 1}`)}
        {monthLabelVariant === "monthAndYear" && ` ${date.getFullYear()}`}
        {monthLabelVariant === "onlyMonth" && (month === 0 || month === 11)
          ? ` ${date.getFullYear()}`
          : ""}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Helmet>
        <style>{getStyling(props.variant)}</style>
      </Helmet>
      <TestWrapper testId={RANGE_PICKER.CONTAINER}>
        <div>
          <DayPickerMobileMonths>
            {mobileWeekdaysShort.map((item, idx) => (
              <span key={idx}>{item}</span>
            ))}
          </DayPickerMobileMonths>
          <Picker
            className={props.selectable ? "Selectable" : ""}
            numberOfMonths={isMobile ? 12 : 3}
            modifiers={modifiers}
            canChangeMonth={!isMobile}
            showOutsideDays
            firstDayOfWeek={1}
            // @ts-ignore
            selectedDays={selectedDays} // I had to use ts ignore because there is a typing error in DayPicker
            onDayClick={handleDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            weekdaysShort={weekdaysShort}
            navbarElement={renderNavbar}
            captionElement={renderCaption}
            onMonthChange={props.onMonthChange}
            {...(isValid(props.calendarStartDate) && {
              month: props.calendarStartDate,
            })}
          />
        </div>
      </TestWrapper>
    </React.Fragment>
  );
};

export { RangePicker };
